import { useDialog } from '@works-web/settings/modules/dialog';
import { useToast } from '@works-web/settings/modules/toast';
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { RadioGroup, Tooltip, Select } from '~/settings/__modules__/component';
import BgColor, { bgColorById } from '~/settings/__modules__/component/BgColor';
import { useModal } from '~/settings/__modules__/modal';
import { FONT_SIZE_STYLE, GeneralBiz } from '~/settings/biz/GeneralBiz';
import {
  CommonService,
  UserOptionName,
} from '~/settings/service/CommonService';

export function General() {
  const { t } = useTranslation();
  const toast = useToast();
  const dialog = useDialog();
  const { pushModal } = useModal();

  const [sendKey, setSendKey] = useState(GeneralBiz.getSendMsgShortCutOption());
  const [fontsize, setFontsize] = useState(GeneralBiz.getFontSizeOption());
  const [translate, setTranslate] = React.useState();
  const [translateOptions, setTransOptions] = React.useState([]);
  const [initialBgColor, setInitialBgColor] = useState();

  const sendKeyOptions = [
    { value: 'Enter', label: t('talk_main_sendkey1') },
    { value: 'CtrlEnter', label: t('talk_main_sendkey2') },
    { value: 'AltEnter', label: t('talk_main_sendkey3') },
  ];

  const fontSizeOptions = [
    { value: FONT_SIZE_STYLE.FONT_MEDIUM, label: t('talk_main_fontsize1') },
    { value: FONT_SIZE_STYLE.FONT_LARGE, label: t('talk_main_fontsize2') },
  ];

  const save = function (key, value) {
    let params = Object.assign(
      {
        sendKey,
        fontsize,
        translate,
      },
      { [key]: value },
    );
    GeneralBiz.save(params)
      .then((isSuccess) => {
        if (isSuccess) {
          toast(t('saved'));
          if (key === 'sendKey') {
            setSendKey(value);
          }
          if (key === 'fontsize') {
            setFontsize(value);
          }
          if (key === 'translate') {
            setTranslate(value);
          }
        } else {
          dialog.alert((action) => ({
            title: <Trans i18nKey="error_unknown_title" />,
            description: <Trans i18nKey="error_unknown_description" />,
            [action()]: (close) => {
              close && close();
            },
          }));
        }
      })
      .catch(() => {
        dialog.alert((action) => ({
          title: <Trans i18nKey="error_unknown_title" />,
          description: <Trans i18nKey="error_unknown_description" />,
          [action()]: (close) => {
            close && close();
          },
        }));
      });
  };

  const onSettingsChange = function (key, value) {
    if (
      (key === 'sendKey' && value !== sendKey) ||
      (key === 'fontsize' && value !== fontsize) ||
      (key === 'translate' && value !== translate)
    ) {
      save(key, value);
    }
  };

  const handleClickBackground = (e) => {
    e.preventDefault();
    const save = async (color) => {
      await CommonService.save(true);
      setInitialBgColor(color);
    };
    pushModal(() => <BgColor initialBgColor={initialBgColor} save={save} />);
  };

  const addAlphaIfNeeded = (color) => {
    return color.startsWith('#') && color.length == 7 ? color + 'FF' : color;
  };

  useEffect(() => {
    GeneralBiz.getAutoTranslateOption().then((res) => {
      if (res.selected) {
        setTranslate(res.selected.value);
      }
      setTransOptions(res.list);
    });

    const defaultColor = addAlphaIfNeeded(
      CommonService.getOptionValue(UserOptionName.DefaultChannelBgColor),
    );
    const defaultColorList = Object.values(bgColorById);

    setInitialBgColor(
      defaultColorList.includes(defaultColor)
        ? defaultColor
        : defaultColorList[0],
    );
  }, []);

  const bgColorId = Object.keys(bgColorById).find(
    (key) => bgColorById[key] === initialBgColor,
  );
  return (
    <div className="settings_cover">
      <h3 className="title">
        <span className="text">{t('talk_main_envset')}</span>
      </h3>
      <ul className="settings_list">
        <li className="item">
          <div className="title_area">
            <h4 className="title">
              <span className="text">{t('talk_main_sendkeytitle')}</span>
            </h4>
          </div>
          <RadioGroup
            options={sendKeyOptions}
            value={sendKey}
            onChange={(value) => onSettingsChange('sendKey', value)}
          />
        </li>
        <li className="item">
          <div className="title_area">
            <h4 className="title">
              <span className="text">
                <Trans i18nKey="talk_main_fontsizetitle" />
              </span>
              <Tooltip>
                <Trans i18nKey="talk_main_tooltip1" />
              </Tooltip>
            </h4>
          </div>
          <RadioGroup
            options={fontSizeOptions}
            value={fontsize}
            onChange={(value) => onSettingsChange('fontsize', value)}
          />
        </li>
        <li className="item">
          <div className="title_area">
            <h4 className="title">
              <span className="text">{t('talk_main_translate')}</span>
              <Tooltip>
                <Trans i18nKey="talk_main_tooltip2" />
              </Tooltip>
            </h4>
          </div>
          <div className="task_area">
            <Select
              value={translate}
              onChange={(e) => onSettingsChange('translate', e.target.value)}
            >
              {translateOptions.map((item) => {
                return (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        </li>
        <li className="item has_link">
          <div className="title_area">
            <h4 className="title">
              <span className="text">{t('talk_main_background')}</span>
            </h4>
            <p className="desc">
              {t('talk_main_background_description')
                .split('\\n')
                .map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </p>
          </div>
          <div className={`wallpaper_area ${bgColorId}`}>
            <span className="blind"></span>
          </div>
          <a href="#" className="link_more" onClick={handleClickBackground}>
            <i className="blind"></i>
          </a>
        </li>
      </ul>
    </div>
  );
}
